/**
 * Which of the two entry points this page is.
 *
 * One application, served from two places that are not interchangeable:
 *
 *   - the **account**, on the provider's own domain, entered with a provider
 *     account. Subscription, invoices, tenancies, and the choice of how people
 *     will sign in to the cluster. Both provider operators and a tenant's own
 *     representatives come here.
 *   - the **cluster console**, on a tenancy's domain, entered through the
 *     identity provider that tenancy chose. Resources, access, sessions,
 *     recordings.
 *
 * Which one a page is follows from the host it was served from — not from a
 * switch, not from a preference, not from a route. That is the point: the two
 * entry points have different sign-ins and different blast radii, so the
 * decision has to be made somewhere a person cannot reach. A compromised
 * account console opens invoices and provisioning; it does not open a tenant's
 * hosts, because what opens those is the tenant's own identity provider on a
 * different origin.
 *
 * Everything downstream keys off the value this module returns: the routes
 * that parse, the API surface that exists, the session type, the screens.
 *
 * **The host is read by the server, not here.** The control plane already
 * decides both trees off the domain, and until it was asked this build carried
 * a copy of that decision — the account's host, compiled in. A copy can be
 * wrong, and twice it was: built without the value, every host reads as the
 * account, and a tenancy's console showed the provider's sign-in form. So this
 * module now parses an answer instead of making one.
 */

export type EntryPoint =
  | { readonly kind: 'account' }
  | { readonly kind: 'cluster'; readonly tenancyDomain: string };

export type EntryKind = EntryPoint['kind'];

/**
 * What `GET /api/entry` said, as an entry point — or a refusal.
 *
 * Strict about the shape rather than lenient: this decides which console a
 * person is shown, so an answer that is not one of the two known shapes is an
 * answer we did not understand, and guessing at it is how the wrong console
 * gets mounted. `null` means the caller shows that the console cannot start.
 */
export function entryFromAnswer(value: unknown): EntryPoint | null {
  if (typeof value !== 'object' || value === null) return null;
  const answer = value as { tree?: unknown; console_domain?: unknown };
  if (answer.tree === 'account') {
    return { kind: 'account' };
  }
  if (answer.tree === 'cluster' && typeof answer.console_domain === 'string') {
    const domain = answer.console_domain.trim();
    if (domain !== '') {
      return { kind: 'cluster', tenancyDomain: domain };
    }
  }
  return null;
}
